import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const CookiePolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0a0a2e] text-white py-24">
      <div className="max-w-4xl mx-auto px-6">
        <Button
          onClick={() => navigate('/')}
          variant="outline"
          className="mb-8 border-cyan-500/50 hover:border-cyan-400 bg-transparent text-white"
        >
          <ArrowLeft className="mr-2" size={20} />
          Back to Home
        </Button>

        <h1 className="text-4xl lg:text-5xl font-bold mb-4 bg-gradient-to-r from-cyan-400 to-pink-500 bg-clip-text text-transparent">
          Cookie Policy
        </h1>
        <p className="text-gray-400 mb-8">Last updated: December 2025</p>

        <div className="space-y-8 text-gray-300">
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">1. What Are Cookies?</h2>
            <p className="leading-relaxed">
              Cookies are small text files stored on your device when you visit our website. They help us provide you with a better experience by remembering your preferences and analyzing how you use our platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">2. Types of Cookies We Use</h2>
            
            <div className="space-y-6">
              <div className="p-6 bg-gray-900/50 rounded-lg border border-cyan-500/20">
                <h3 className="text-xl font-semibold text-cyan-400 mb-3">Essential Cookies</h3>
                <p className="leading-relaxed mb-2">
                  These cookies are necessary for the platform to function properly.
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Authentication and session management</li>
                  <li>Security features</li>
                  <li>Load balancing</li>
                </ul>
                <p className="mt-3 text-sm text-gray-400">Duration: Session or up to 1 year</p>
              </div>

              <div className="p-6 bg-gray-900/50 rounded-lg border border-purple-500/20">
                <h3 className="text-xl font-semibold text-purple-400 mb-3">Performance Cookies</h3>
                <p className="leading-relaxed mb-2">
                  These cookies help us understand how visitors interact with our platform.
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Page visit tracking</li>
                  <li>Error logging</li>
                  <li>Performance monitoring</li>
                </ul>
                <p className="mt-3 text-sm text-gray-400">Duration: Up to 2 years</p>
              </div>

              <div className="p-6 bg-gray-900/50 rounded-lg border border-pink-500/20">
                <h3 className="text-xl font-semibold text-pink-400 mb-3">Functionality Cookies</h3>
                <p className="leading-relaxed mb-2">
                  These cookies enable enhanced functionality and personalization.
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Language preferences</li>
                  <li>User interface customization</li>
                  <li>Previously viewed content</li>
                </ul>
                <p className="mt-3 text-sm text-gray-400">Duration: Up to 1 year</p>
              </div>

              <div className="p-6 bg-gray-900/50 rounded-lg border border-blue-500/20">
                <h3 className="text-xl font-semibold text-blue-400 mb-3">Marketing Cookies</h3>
                <p className="leading-relaxed mb-2">
                  These cookies track your activity to show relevant advertisements.
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Advertisement targeting</li>
                  <li>Campaign effectiveness</li>
                  <li>Social media integration</li>
                </ul>
                <p className="mt-3 text-sm text-gray-400">Duration: Up to 2 years</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">3. Third-Party Cookies</h2>
            <p className="leading-relaxed mb-4">
              We use services from third-party providers that may set their own cookies:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Google Analytics:</strong> For understanding user behavior and platform performance</li>
              <li><strong>Payment Processors:</strong> For secure transaction processing</li>
              <li><strong>Social Media Platforms:</strong> For social sharing features</li>
              <li><strong>Content Delivery Networks:</strong> For faster content delivery</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">4. Managing Cookies</h2>
            <p className="leading-relaxed mb-4">
              You can control and manage cookies in several ways:
            </p>

            <h3 className="text-xl font-semibold text-cyan-400 mb-3">4.1 Browser Settings</h3>
            <p className="leading-relaxed mb-4">
              Most browsers allow you to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>View and delete cookies</li>
              <li>Block third-party cookies</li>
              <li>Block cookies from specific sites</li>
              <li>Block all cookies</li>
              <li>Delete all cookies when you close your browser</li>
            </ul>

            <h3 className="text-xl font-semibold text-cyan-400 mb-3 mt-6">4.2 Browser-Specific Instructions</h3>
            <div className="space-y-2 ml-4">
              <p><strong>Chrome:</strong> Settings → Privacy and security → Cookies and other site data</p>
              <p><strong>Firefox:</strong> Options → Privacy & Security → Cookies and Site Data</p>
              <p><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</p>
              <p><strong>Edge:</strong> Settings → Cookies and site permissions → Manage cookies</p>
            </div>

            <p className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-yellow-200">
              <strong>Note:</strong> Blocking or deleting cookies may affect your ability to use certain features of our platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">5. Cookie Consent</h2>
            <p className="leading-relaxed">
              When you first visit our platform, we will ask for your consent to use non-essential cookies. You can withdraw your consent at any time by adjusting your cookie preferences in your browser settings.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">6. Do Not Track Signals</h2>
            <p className="leading-relaxed">
              Some browsers support "Do Not Track" signals. Our platform currently does not respond to Do Not Track signals, but you can manage cookies through your browser settings as described above.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">7. Updates to This Policy</h2>
            <p className="leading-relaxed">
              We may update this Cookie Policy to reflect changes in our practices or for legal reasons. We will notify you of significant changes by updating the "Last updated" date at the top of this page.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">8. Contact Us</h2>
            <p className="leading-relaxed mb-4">
              If you have questions about our use of cookies, please contact us:
            </p>
            <div className="p-6 bg-gray-900/50 rounded-lg border border-gray-700/50">
              <p className="text-cyan-400 font-semibold">StreamShares</p>
              <p>Email: info@streamshares.co.uk</p>
              <p>Location: London, United Kingdom</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;
